import type { GraphData } from '../roads/graph';
import { hash01 } from '../rng';
import { MeshBuilder } from './builder';
import { ROAD_WIDTH, roadZ } from './roadMesh';

/**
 * Road decals of one tile, for its own edges (DECISIONS "Street detail"): crosswalks and stop lines
 * at junctions, centre lines (double yellow on main roads, dashed white on secondary ones), manholes,
 * drains, oil stains, cracks and patches. Quads carry UVs into the decal atlas (DECAL_CELLS, drawn in
 * src/render/streetAssets.ts) and are subdivided and draped on the road surface 4 cm up; the
 * renderer adds a polygon offset on top.
 */

/** Atlas cells (4 × 4 grid): index → what's drawn there. */
export const DECAL_CELLS = { white: 0, yellow: 1, manhole: 2, drain: 3, stain: 4, crack: 5, patch: 6 } as const;
const GRID = 4;
const LIFT = 4;

/** Junction clearance from a node of degree 3+ to where its approaches' markings start (cm). */
export const JUNCTION_CLEAR = ROAD_WIDTH / 2 + 150;

interface Frame {
  x: number;
  y: number;
  dx: number;
  dy: number;
}

/**
 * A decal rectangle centred at (x, y), `len` along (dx, dy) and `wid` across, textured with atlas
 * `cell`. Subdivided every 2 m so it follows the terrain.
 */
function decal(mb: MeshBuilder, z: (x: number, y: number) => number, f: Frame, len: number, wid: number, cell: number): void {
  const nx = -f.dy;
  const ny = f.dx;
  const cu = (cell % GRID) / GRID;
  const cv = 1 - (Math.floor(cell / GRID) + 1) / GRID;
  const su = Math.max(1, Math.ceil(len / 200));
  const sv = Math.max(1, Math.ceil(wid / 200));
  const base = mb.vertexCount;
  for (let i = 0; i <= su; i++)
    for (let j = 0; j <= sv; j++) {
      const s = (i / su - 0.5) * len;
      const t = (j / sv - 0.5) * wid;
      const x = f.x + f.dx * s + nx * t;
      const y = f.y + f.dy * s + ny * t;
      // Inset the UVs a little so mipmaps don't bleed in the neighbouring cells.
      const u = cu + (0.02 + 0.96 * (i / su)) / GRID;
      const v = cv + (0.02 + 0.96 * (j / sv)) / GRID;
      mb.vertex(x, y, z(x, y) + LIFT, 0, 0, 1, 1, 1, 1, u, v);
    }
  for (let i = 0; i < su; i++)
    for (let j = 0; j < sv; j++) {
      const a = base + i * (sv + 1) + j;
      const b = a + sv + 1;
      mb.tri(a, b, b + 1);
      mb.tri(a, b + 1, a + 1);
    }
}

/** Node degrees over the tile window (nodes keyed by coordinates, as gates weld across tiles). */
export function degrees(window: GraphData[]): Map<string, number> {
  const deg = new Map<string, number>();
  const seen = new Set<string>();
  for (const g of window)
    for (let e = 0; e < g.edges.length; e += 2) {
      const ka = `${g.nodes[g.edges[e] * 2]},${g.nodes[g.edges[e] * 2 + 1]}`;
      const kb = `${g.nodes[g.edges[e + 1] * 2]},${g.nodes[g.edges[e + 1] * 2 + 1]}`;
      const ke = ka < kb ? `${ka}|${kb}` : `${kb}|${ka}`;
      if (seen.has(ke)) continue;
      seen.add(ke);
      deg.set(ka, (deg.get(ka) ?? 0) + 1);
      deg.set(kb, (deg.get(kb) ?? 0) + 1);
    }
  return deg;
}

export function buildDecals(seed: number, own: GraphData, window: GraphData[]): MeshBuilder {
  const mb = new MeshBuilder();
  const z = roadZ(seed);
  const deg = degrees(window);
  const W = ROAD_WIDTH;
  for (let e = 0; e < own.edges.length / 2; e++) {
    const ia = own.edges[e * 2];
    const ib = own.edges[e * 2 + 1];
    const ax = own.nodes[ia * 2];
    const ay = own.nodes[ia * 2 + 1];
    const bx = own.nodes[ib * 2];
    const by = own.nodes[ib * 2 + 1];
    const L = Math.hypot(bx - ax, by - ay);
    if (L < 100) continue;
    const dx = (bx - ax) / L;
    const dy = (by - ay) / L;
    const nx = -dy;
    const ny = dx;
    const main = own.types[e] === 0;
    const at = (s: number, t: number, ddx = dx, ddy = dy): Frame => ({ x: ax + dx * s + nx * t, y: ay + dy * s + ny * t, dx: ddx, dy: ddy });
    const junctionA = (deg.get(`${ax},${ay}`) ?? 0) >= 3;
    const junctionB = (deg.get(`${bx},${by}`) ?? 0) >= 3;
    // Crosswalks and stop lines at junction ends. Traffic drives on the right: arriving at b it is on
    // the −n side, arriving at a on the +n side.
    const ends: [number, number, number][] = [];
    if (junctionA && L > 2 * JUNCTION_CLEAR + 600) ends.push([JUNCTION_CLEAR + 150, 1, JUNCTION_CLEAR + 300 + 40]);
    if (junctionB && L > 2 * JUNCTION_CLEAR + 600) ends.push([L - JUNCTION_CLEAR - 150, -1, L - JUNCTION_CLEAR - 300 - 40]);
    for (const [s, side, stop] of ends) {
      for (let t = -W / 2 + 90; t <= W / 2 - 90; t += 100) decal(mb, z, at(s, t), 300, 50, DECAL_CELLS.white);
      decal(mb, z, at(stop, (side * W) / 4), 40, W / 2 - 60, DECAL_CELLS.white);
    }
    // Centre line between the crosswalks (or the ends).
    const s0 = junctionA ? JUNCTION_CLEAR + 360 : 100;
    const s1 = L - (junctionB ? JUNCTION_CLEAR + 360 : 100);
    if (s1 - s0 > 200) {
      if (main) {
        for (const t of [-12, 12]) decal(mb, z, at((s0 + s1) / 2, t), s1 - s0, 10, DECAL_CELLS.yellow);
      } else {
        for (let s = s0; s + 300 <= s1; s += 900) decal(mb, z, at(s + 150, 0), 300, 12, DECAL_CELLS.white);
      }
    }
    // Street furniture of the road surface, every few metres by hash.
    for (let s = 400; s < L - 400; s += 700) {
      const h = (k: number) => hash01(seed, Math.round(ax + dx * s), Math.round(ay + dy * s), k);
      if (h(1) < 0.18) decal(mb, z, at(s, (h(2) - 0.5) * 500), 75, 75, DECAL_CELLS.manhole);
      if (h(3) < 0.25) decal(mb, z, at(s + 200, (h(4) < 0.5 ? -1 : 1) * (W / 2 - 35)), 70, 36, DECAL_CELLS.drain);
      if (h(5) < 0.35) {
        const r = h(6) * Math.PI * 2;
        decal(mb, z, at(s + 350, (h(7) - 0.5) * 700, Math.cos(r), Math.sin(r)), 100 + h(8) * 200, 80 + h(9) * 150, DECAL_CELLS.stain);
      }
      if (h(10) < 0.2) {
        const r = h(11) * Math.PI;
        decal(mb, z, at(s + 100, (h(12) - 0.5) * 800, Math.cos(r), Math.sin(r)), 200 + h(13) * 250, 120, DECAL_CELLS.crack);
      }
      if (h(14) < 0.08) decal(mb, z, at(s + 500, (h(15) < 0.5 ? -1 : 1) * W * 0.25), 250 + h(16) * 300, 180, DECAL_CELLS.patch);
    }
  }
  return mb;
}
